import get from 'lodash/get'
import intersection from 'lodash/intersection'
import * as React from 'react'

import type { CustomContactField } from 'types/list'
import type { DropdownOption } from 'types/ui'

import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'

import ContactCard from '../contact-card'
import SelectedList from './selected-list'
import styles from './index.module.css'

const proPromptTextCreator = label => `Create new PRO "${label}"`

const publisherPromptTextCreator = label => `Create new publisher "${label}"`

const emptyArray = []
type Props = {
  contact: Record<string, any>
  pros: Record<string, any>[]
  publishers: Record<string, any>[]
  editMode: boolean
  onFieldChange: (...args: Array<any>) => any
  onCustomFieldChange: (...args: Array<any>) => any
  onUpdate: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
  publisherDropdownOptions: DropdownOption[]
  proDropdownOptions: DropdownOption[]
  onProSelect: (...args: Array<any>) => any
  onPublisherSelect: (...args: Array<any>) => any
  onDefaultProSelect: (...args: Array<any>) => any
  onDefaultPublisherSelect: (...args: Array<any>) => any
  onSwitchToEditMode: (...args: Array<any>) => any
  saving?: boolean
  customFields: CustomContactField[]
}
const writerTypes = ['Writer', 'Artist']

class DetailsTab extends React.Component<Props> {
  render(): React.ReactNode {
    const {
      contact: {
        name,
        email,
        company,
        phone,
        territory,
        tags,
        pros,
        publishers,
        acts,
        disco_user,
        referenced_business,
        pendingChanges,
        default_pro,
        default_publisher,
        custom_data,
      },
      publishers: publisherList,
      pros: proList,
      proDropdownOptions,
      publisherDropdownOptions,
      editMode,
      saving,
      onFieldChange,
      onCustomFieldChange,
      onUpdate,
      onCancel,
      onProSelect,
      onPublisherSelect,
      onDefaultPublisherSelect,
      onDefaultProSelect,
      onSwitchToEditMode,
      customFields,
    } = this.props
    const nameValue = get(pendingChanges, 'name', name || '')
    const emailValue = get(pendingChanges, 'email', email || '')
    const companyValue = get(pendingChanges, 'company', company || '')
    const phoneValue = get(pendingChanges, 'phone', phone || '')
    const territoryValue = get(pendingChanges, 'territory', territory || '')
    const tagsValue = get(pendingChanges, 'type', tags || [])
    const proValue = get(pendingChanges, 'pros', pros || emptyArray).join(',')
    const defaultProValue = get(
      pendingChanges,
      'default_pro',
      default_pro || null,
    )
    const defaultPro = defaultProValue
      ? proList.find(p => p.id === defaultProValue)
      : null
    const defaultPublisherValue = get(
      pendingChanges,
      'default_publisher',
      default_publisher || null,
    )
    const defaultPublisher = defaultPublisherValue
      ? publisherList.find(p => p.id === defaultPublisherValue)
      : null
    const publisherValue = get(
      pendingChanges,
      'publishers',
      publishers || emptyArray,
    ).join(',')
    const actsValue = get(pendingChanges, 'acts', acts || '')
    const showExtraFields = intersection(writerTypes, tagsValue).length > 0
    const publishersArray = (publishers || emptyArray)
      .map(id => {
        const publisher = publisherList.find(p => p.id === id)
        return publisher ? publisher.name : null
      })
      .filter(Boolean)
    const proArray = (pros || emptyArray)
      .map(id => {
        const pro = proList.find(p => p.id === id)
        return pro ? pro.name : null
      })
      .filter(Boolean)
    return editMode ? (
      <ContactCard>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Name"
              name="name"
              value={nameValue}
              onChange={onFieldChange}
              required
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Email"
              name="email"
              value={emailValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Company"
              name="company"
              value={companyValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Phone"
              name="phone"
              value={phoneValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <TextField
              label="Location"
              name="territory"
              value={territoryValue}
              onChange={onFieldChange}
            />
          </div>
        </Row>
        {showExtraFields && (
          <React.Fragment>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label caption="Publishers" />
                <CustomDropdown
                  name="publisher"
                  placeholder="Select publishers..."
                  promptTextCreator={publisherPromptTextCreator}
                  multi
                  onChange={onPublisherSelect}
                  value={publisherValue}
                  options={publisherDropdownOptions}
                  allowCreate
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label="Acts"
                  name="acts"
                  value={actsValue}
                  onChange={onFieldChange}
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom>
              <div>
                <Label caption="PRO" />
                <CustomDropdown
                  name="pro"
                  placeholder="Select PRO..."
                  promptTextCreator={proPromptTextCreator}
                  multi
                  onChange={onProSelect}
                  value={proValue}
                  options={proDropdownOptions}
                  allowCreate
                />
              </div>
            </Row>
          </React.Fragment>
        )}
        {customFields.map(field => {
          const fieldValue = get(
            pendingChanges,
            `custom_data.${field.name}`,
            custom_data[field.name] || '',
          )
          return (
            <Row key={field.name} columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label={field.name}
                  name={field.name}
                  value={fieldValue}
                  onChange={onCustomFieldChange}
                />
              </div>
            </Row>
          )
        })}
        <ButtonContainer>
          <Button
            appearance="primary"
            caption="Update contact details"
            disabled={!!referenced_business}
            onClick={onUpdate}
            progressIndicator={saving}
            progressCaption="Updating contact"
          />
          <Button caption="Cancel" onClick={onCancel} />
        </ButtonContainer>
      </ContactCard>
    ) : (
      <ContactCard
        action={{
          content: 'Edit',
          disabled: !!referenced_business,
          onClick: onSwitchToEditMode,
        }}
      >
        <div>
          <strong>{name}</strong>
        </div>
        <div>{email}</div>
        <table className={styles.detailsTab}>
          <tbody>
            <tr>
              <td className={styles.detailsTabFieldCaption}>Company</td>
              <td>{company}</td>
            </tr>
            {disco_user &&
              disco_user.businesses &&
              disco_user.businesses.length > 0 && (
                <tr>
                  <td className={styles.detailsTabFieldCaption}>DISCOs</td>
                  <td>{disco_user.businesses.map(b => b.name).join(', ')}</td>
                </tr>
              )}
            <tr>
              <td className={styles.detailsTabFieldCaption}>Phone</td>
              <td>{phone}</td>
            </tr>
            <tr>
              <td className={styles.detailsTabFieldCaption}>Location</td>
              <td>{territory}</td>
            </tr>
            {showExtraFields && (
              <React.Fragment>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>Publishers</td>
                  <td>
                    <SelectedList
                      list={publishersArray}
                      selectCaption="make default"
                      selectedItem={
                        defaultPublisher ? defaultPublisher.name : undefined
                      }
                      onSelect={onDefaultPublisherSelect}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>Acts</td>
                  <td>{acts}</td>
                </tr>
                <tr>
                  <td className={styles.detailsTabFieldCaption}>PRO</td>
                  <td>
                    <SelectedList
                      list={proArray}
                      selectCaption="make default"
                      selectedItem={defaultPro ? defaultPro.name : undefined}
                      onSelect={onDefaultProSelect}
                    />
                  </td>
                </tr>
              </React.Fragment>
            )}
            {customFields.map(field => (
              <tr key={field.name}>
                <td className={styles.detailsTabFieldCaption}>{field.name}</td>
                <td>{custom_data[field.name]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ContactCard>
    )
  }
}

export default DetailsTab
